import { motion } from "framer-motion";
import {
  MenuIcon,
  DoubleChevronLeft,
  IconButton,
} from "@datapay-by-datacom/design-system";
import { IoSettingsOutline } from "react-icons/io5";
import { Profile } from "../../profile";
import { NotificationAlert } from "../../notifications/components/NotificationAlert";
import { ReleaseBanner } from "../../announcements/components/ReleaseBanner";
import { McpKeyExpiryAlert } from "../../mcp-keys/components/McpKeyExpiryAlert";
import s from "./Layout.module.css";
import { SidebarMenu, SidebarVersionInfo, SidebarFooter } from "./SidebarMenu";
import { useNavigate } from "react-router";
import { FeatureFlag, useFeatureFlagValue } from "../../feature-flags";
import { useSidebarResize } from "../hooks/useSidebarResize";

interface LayoutProps {
  children: React.ReactNode;
  sidebarChildren?: React.ReactNode;
  wide?: boolean;
  /** Places global controls in a dedicated row instead of overlaying content. */
  utilityHeader?: boolean;
}

export function Layout({
  children,
  sidebarChildren,
  wide,
  utilityHeader = false,
}: LayoutProps) {
  const navigate = useNavigate();
  const {
    isCollapsed,
    sidebarWidth,
    isDragging,
    sidebarRef,
    toggleSidebar,
    onResizeStart,
  } = useSidebarResize();
  const mcpKeysEnabled = useFeatureFlagValue(FeatureFlag.EnableMcpKeysInterface);
  const userControls = (
    <>
      {mcpKeysEnabled && <McpKeyExpiryAlert />}
      <NotificationAlert />
      <IconButton onPress={() => navigate("/settings")} aria-label="Settings">
        <IoSettingsOutline size={20} />
      </IconButton>
      <Profile />
    </>
  );
  const mainContent = (
    <main className={`${s.content} ${!isCollapsed ? s.overlay : ""}`}>
      <ReleaseBanner />
      <div className={wide ? s.contentInnerWide : s.contentInner}>
        {children}
      </div>
    </main>
  );

  return (
    <>
      <div className={s.container}>
        {/* Sidebar with motion */}
        <motion.div
          ref={sidebarRef}
          initial={false}
          className={s.sidebar}
          animate={{ width: sidebarWidth }}
          transition={
            isDragging.current
              ? { duration: 0 }
              : { type: "spring", stiffness: 300, damping: 30 }
          }
        >
          <div className={s.sidebarContent}>
            <div className={s.menuItems}>
              <SidebarMenu />
              <div className={s.sidebarScrollable}>
                {sidebarChildren}
              </div>
              <div className={s.sidebarBottom}>
                <SidebarFooter />
                <SidebarVersionInfo />
              </div>
            </div>
          </div>
          {!isCollapsed && (
            <div
              className={s.resizeHandle}
              onPointerDown={onResizeStart}
            />
          )}
        </motion.div>

        <IconButton className={s.toggleButton} onPress={toggleSidebar} aria-label={isCollapsed ? "Open sidebar" : "Close sidebar"}>
          {isCollapsed ? <MenuIcon /> : <DoubleChevronLeft />}
        </IconButton>

        {utilityHeader ? (
          <div className={s.contentColumn}>
            <header
              className={s.utilityHeader}
              aria-label="Account and application controls"
            >
              {userControls}
            </header>
            {mainContent}
          </div>
        ) : (
          <>
            {mainContent}
            <div className={s.userControls}>{userControls}</div>
          </>
        )}
      </div>
    </>
  );
}
